import ApartmentOutlined from '@ant-design/icons/lib/icons/ApartmentOutlined'
import ClockCircleOutlined from '@ant-design/icons/lib/icons/ClockCircleOutlined'
import UserOutlined from '@ant-design/icons/lib/icons/UserOutlined'
import { Timeline } from 'antd'
import { inject, observer } from 'mobx-react'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import FileItem from '../../../../components/Common/FileItemComponent/FileItem'
import { ELECTRONIC_DOCUMENT_STATUS } from '../../../../constants'
import dayjs from '../../../../dayjsConfig'
import utils from '../../../../utils'

const ELECTRONIC_STATUS = [
  'PENDING',
  'CRE_APPROVAL',
  'TP_APPROVAL',
  'VT_APPROVAL',
  'VT_SIGNED',
  'VT_REJECTION',
  'LEAD_APPROVAL',
  'LEAD_REJECTION',
  'REGIS_NUMBER',
  'REGIS_NUMBER_REJECTION',
  'READY',
  'READY_REJECTION',
  'RELEASE',
]

const STATUS_MAPPING = {
  [ELECTRONIC_DOCUMENT_STATUS.PENDING]: 'Chưa xử lý',
  [ELECTRONIC_DOCUMENT_STATUS.CHANGE_DOC]: 'Thay đổi tài liệu',
  [ELECTRONIC_DOCUMENT_STATUS.CRE_APPROVAL]: 'Đã trình dự thảo',
  [ELECTRONIC_DOCUMENT_STATUS.TP_APPROVAL]: 'Trưởng phòng đã ký số',
  [ELECTRONIC_DOCUMENT_STATUS.VT_APPROVAL]: 'Văn thư đã duyệt',
  [ELECTRONIC_DOCUMENT_STATUS.VT_SIGNED]: 'Văn thư đã ký số',
  [ELECTRONIC_DOCUMENT_STATUS.VT_REJECTION]: 'Văn thư hủy duyệt',
  [ELECTRONIC_DOCUMENT_STATUS.LEAD_APPROVAL]: 'Lãnh đạo đã ký số',
  [ELECTRONIC_DOCUMENT_STATUS.LEAD_REJECTION]: 'Lãnh đạo hủy duyệt',
  [ELECTRONIC_DOCUMENT_STATUS.REGIS_NUMBER]: 'Đã cấp số',
  [ELECTRONIC_DOCUMENT_STATUS.REGIS_NUMBER_REJECTION]: 'Cấp lại số',
  [ELECTRONIC_DOCUMENT_STATUS.READY]: 'Chờ phát hành',
  [ELECTRONIC_DOCUMENT_STATUS.READY_REJECTION]: 'Ký số lại',
  [ELECTRONIC_DOCUMENT_STATUS.RELEASE]: 'Đã phát hành',
  [ELECTRONIC_DOCUMENT_STATUS.ANNEX_DOC]: 'Đã xử lý phụ lục đi kèm',
  [ELECTRONIC_DOCUMENT_STATUS.ANNEX_REGIS_NUMBER]: 'Đã cấp số phụ lục',
  [ELECTRONIC_DOCUMENT_STATUS.ANNEX_READY]: 'Đã ký số phụ lục',
  [ELECTRONIC_DOCUMENT_STATUS.TP_REVERT]: 'Trưởng phòng hủy duyệt',
  [ELECTRONIC_DOCUMENT_STATUS.REGIS_NUMBER_REVERT]: 'Hủy cấp số',
  [ELECTRONIC_DOCUMENT_STATUS.RELEASE_REJECTION]: 'Phát hành lại',
  [ELECTRONIC_DOCUMENT_STATUS.TP_CO_SIGN]: 'Trưởng phòng ký',
}

const DEFAULT_USER_NAME = 'Admin vpdt'
const DEFAULT_DEPARTMENT = 'Mặc định'

const getActionColor = action => {
  switch (action) {
    case 'PENDING':
      return 'red'
    case 'RELEASE':
      return 'green'
    default:
      return 'blue'
  }
}

const parseAnnexFiles = dataAfter => {
  try {
    const parsedData = JSON.parse(dataAfter)
    const annexList = Array.isArray(parsedData)
      ? parsedData?.[0]?.annexAttachments
      : parsedData?.annexAttachments

    return annexList || []
  } catch (e) {
    return []
  }
}

const FileRenderer = memo(({ item }) => {
  const { file_id, file_name, action, data_after } = item

  if (file_id && file_name && action !== 'PENDING') {
    return (
      <FileItem
        file_id={file_id}
        file_name={file_name}
        file_type={utils.getExtensionFile(file_name)}
      />
    )
  }

  const annexFiles = parseAnnexFiles(data_after)

  return annexFiles.map(file => (
    <FileItem
      key={file.fileId}
      file_id={file.fileId}
      file_name={file.fileName}
      file_type={utils.getExtensionFile(file.fileName)}
    />
  ))
})

FileRenderer.displayName = 'FileRenderer'

const TimelineItemComponent = memo(
  ({ item, getFullNameByUserName, getDepartmentByUserName }) => {
    const text = STATUS_MAPPING[item.action] || 'Không xác định'
    const userName = item.user?.user_name || 'unknown'
    const actionColor = getActionColor(item.action)
    const processedDate = dayjs(item.processed_date).format(
      'dddd, DD/MM/YYYY, HH:mm:ss'
    )

    return (
      <Timeline.Item
        key={item.id}
        dot={<ClockCircleOutlined style={{ fontSize: '16px' }} />}
        color={actionColor}>
        <div style={{ marginBottom: 4 }}>
          <span style={{ fontWeight: 600 }}>{text}</span>
        </div>

        <div style={{ marginBottom: 4 }}>
          <span style={{ color: 'gray' }}>
            {utils.getNameInCapitalize(processedDate)}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <UserOutlined style={{ marginRight: 8 }} />
          <span>
            {utils.getNameInCapitalize(getFullNameByUserName(userName))}
          </span>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 4,
          }}>
          <ApartmentOutlined style={{ marginRight: 8 }} />
          <span>{getDepartmentByUserName(userName)}</span>
        </div>

        <FileRenderer item={item} />
        {item.action === 'RELEASE' && (() => {
          let companyText = item.data_after
          try {
            const parsed = JSON.parse(item.data_after)
            if (typeof parsed === 'object') {
              companyText = parsed?.companies || parsed?.[0]?.companies || ''
            }
          } catch (e) {
            // Keep original string if not JSON
          }
          
          if (!companyText) return null
          
          return (
            <div>
              <span style={{ marginRight: 8 }}>Công ty nhận: </span>
              <span>{companyText}</span>
            </div>
          )
        })()}
      </Timeline.Item>
    )
  }
)

TimelineItemComponent.displayName = 'TimelineItemComponent'

const InternalDocumentSignDocHistory = ({
  loadingAnimationStore,
  authenticationStore,
  departmentStore,
  userStore,
  historyLists,
}) => {
  const [userMap, setUserMap] = useState(new Map())

  // Fetch users data once on mount
  useEffect(() => {
    let isMounted = true

    const fetchUserData = async () => {
      try {
        loadingAnimationStore.showSpinner(true)

        await departmentStore.getDepartmentWithUsers(
          authenticationStore.currentCompanyCode()
        )

        if (!isMounted) return

        // Create a Map for O(1) lookup performance
        const userLookupMap = new Map()

        departmentStore.departmentList
          .filter(dept => dept.users?.length > 0)
          .forEach(dept => {
            dept.users.forEach(user => {
              userLookupMap.set(user.user_name, {
                fullName: user.name_uppercase,
                department: dept.name,
              })
            })
          })

        setUserMap(userLookupMap)
      } catch (error) {
        console.error('Error fetching user list:', error)
      } finally {
        if (isMounted) {
          loadingAnimationStore.showSpinner(false)
        }
      }
    }

    fetchUserData()

    return () => {
      isMounted = false
    }
  }, [authenticationStore, departmentStore, loadingAnimationStore])

  // Memoized sorted history list
  const sortedHistoryList = useMemo(() => {
    return [...historyLists].sort((a, b) =>
      dayjs(b.processed_date).diff(dayjs(a.processed_date))
    )
  }, [historyLists])

  // Memoized user lookup functions
  const getFullNameByUserName = useCallback(
    userName => {
      return userMap.get(userName)?.fullName || DEFAULT_USER_NAME
    },
    [userMap]
  )

  const getDepartmentByUserName = useCallback(
    userName => {
      return userMap.get(userName)?.department || DEFAULT_DEPARTMENT
    },
    [userMap]
  )

  // Memoized timeline items
  const timelineItems = useMemo(() => {
    return sortedHistoryList.map(item => (
      <TimelineItemComponent
        key={item.id}
        item={item}
        getFullNameByUserName={getFullNameByUserName}
        getDepartmentByUserName={getDepartmentByUserName}
      />
    ))
  }, [sortedHistoryList, getFullNameByUserName, getDepartmentByUserName])

  return <Timeline mode="left" style={{ paddingLeft: 6 }}>{timelineItems}</Timeline>
}

// Export constants for external use
export {
  ELECTRONIC_STATUS as electronicStatus,
  STATUS_MAPPING as getStatusMapping,
}

export default memo(
  inject(
    'loadingAnimationStore',
    'authenticationStore',
    'userStore',
    'listHistoryLogStore',
    'departmentStore'
  )(observer(InternalDocumentSignDocHistory))
)
